import type { QueryRowType } from "@rocicorp/zero";
import { useMemo } from "react";
import { queries } from "../../shared/queries";
import { useQuery } from "./useQuery";

type ConnectorUsageSpace = QueryRowType<
  ReturnType<typeof queries.connectorUsageDailyBySpace>
>;
type ConnectorUsageWorkflow = ConnectorUsageSpace["workflows"][number];
export type ConnectorUsageDailyRow =
  ConnectorUsageWorkflow["connectorUsageDaily"][number];

type ConnectorUsageDailyArgs = {
  since: number;
  measures?: string[];
  connectorIds?: string[];
};

export function useConnectorUsageDaily(
  args: ConnectorUsageDailyArgs,
  { enabled = true }: { enabled?: boolean } = {}
) {
  const [spaces, attributedResult] = useQuery(
    queries.connectorUsageDailyBySpace(args),
    { enabled }
  );
  const [unattributedRows, unattributedResult] = useQuery(
    queries.connectorUsageDailyWithoutWorkflow(args),
    { enabled }
  );
  const workflows = useMemo(
    () =>
      (spaces ?? []).flatMap(({ workflows: spaceWorkflows, ...space }) =>
        spaceWorkflows.map(({ connectorUsageDaily, ...workflow }) => ({
          ...workflow,
          space,
        }))
      ),
    [spaces]
  );
  const attributedRows = useMemo(
    () =>
      (spaces ?? []).flatMap((space) =>
        space.workflows.flatMap((workflow) => workflow.connectorUsageDaily)
      ),
    [spaces]
  );
  const rows = useMemo(
    () => [...(attributedRows ?? []), ...(unattributedRows ?? [])],
    [attributedRows, unattributedRows]
  );
  const error = [attributedResult, unattributedResult].find(
    (result) => result.type === "error"
  );
  const result =
    error ??
    (enabled &&
    attributedResult.type === "complete" &&
    unattributedResult.type === "complete"
      ? ({ type: "complete" } as const)
      : ({ type: "unknown" } as const));

  return [rows, result, workflows] as const;
}
